import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, Loader2 } from 'lucide-react'
import { getUser, saveUser, type YepUser } from '../../lib/api/users'
import { Field, TextInput, SelectInput, PasswordInput, SwitchRow } from '../config/ui'

const LANGS = [
  { value: 'it', label: 'Italiano' },
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
]

const EMPTY: YepUser = { username: '', name: '', email: '', language: 'it', enabled: true }

export function UserDrawer({ target, onClose }: { target: YepUser | 'new' | null; onClose: () => void }) {
  const qc = useQueryClient()
  const isNew = target === 'new'
  const id = target && target !== 'new' ? target.id : undefined

  // When editing, round-trip the full entity so untouched fields are preserved.
  const full = useQuery({ queryKey: ['operator', id], queryFn: () => getUser(id!), enabled: Boolean(id) })

  const [form, setForm] = useState<YepUser>(EMPTY)
  const [pwd, setPwd] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isNew) setForm(EMPTY)
    else if (full.data) setForm({ ...full.data, password: undefined })
    else if (target) setForm({ ...target, password: undefined })
    setPwd('')
    setError(null)
  }, [isNew, full.data, target])

  const save = useMutation({
    mutationFn: () => saveUser({ ...form, ...(pwd ? { password: pwd } : {}) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operators'] })
      onClose()
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Errore durante il salvataggio'),
  })

  function up<K extends keyof YepUser>(k: K, v: YepUser[K]) {
    setForm((f) => ({ ...f, [k]: v }))
  }

  const canSave = Boolean(form.username.trim() && form.name.trim() && (!isNew || pwd.length > 0))

  if (!target) return null
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col bg-paper shadow-2xl">
        <div className="flex items-center justify-between border-b border-line bg-card px-5 py-4">
          <h3 className="font-display text-[16px] font-bold text-coal">{isNew ? 'Nuovo operatore' : 'Modifica operatore'}</h3>
          <button onClick={onClose} className="text-faint hover:text-slate" aria-label="Chiudi">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-5">
          {id && full.isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-faint" />
            </div>
          ) : (
            <>
              <Field label="Username" hint={isNew ? 'Identificativo di accesso' : 'Non modificabile'}>
                <TextInput value={form.username} onChange={(v) => up('username', v)} disabled={!isNew} mono />
              </Field>
              <Field label="Nome">
                <TextInput value={form.name} onChange={(v) => up('name', v)} placeholder="Nome operatore" />
              </Field>
              <Field label="Email">
                <TextInput value={form.email} onChange={(v) => up('email', v)} placeholder="nome@garagesanmarco.it" />
              </Field>
              <Field label="Lingua">
                <SelectInput value={form.language ?? 'it'} onChange={(v) => up('language', v)} options={LANGS} />
              </Field>
              <Field
                label={isNew ? 'Password' : 'Nuova password'}
                hint={isNew ? 'Richiesta per il nuovo operatore' : 'Lascia vuoto per non modificarla'}
              >
                <PasswordInput value={pwd} onChange={setPwd} placeholder="••••••••" />
              </Field>
              <div className="rounded-lg border border-line bg-card px-4">
                <SwitchRow title="Abilitato" desc="Può accedere al gestionale" on={form.enabled} onToggle={(v) => up('enabled', v)} />
              </div>
              {error && <p className="text-[12.5px] font-medium text-venetian">{error}</p>}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-line bg-card px-5 py-3.5">
          <button onClick={onClose} className="rounded-lg border border-line bg-card px-4 py-2 text-[13px] font-semibold text-slate hover:border-faint">
            Annulla
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={!canSave || save.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-verdigris px-4 py-2 text-[13px] font-semibold text-white hover:bg-verdigris-700 disabled:opacity-50"
          >
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Salva
          </button>
        </div>
      </aside>
    </>
  )
}
